/**
 * SchedaProgetto.jsx
 *
 * GRIGLIA PIXEL-PERFECT:
 *   col 1 (Obiettivo) : 350px  – span righe 1-3
 *   col 2,3,4         : 294px  – singola
 *   col wide          : 588px  – doppia (2×294)
 *
 *   Riga 1: col2=294 | col3=294 | col4=588 (wide)   → Allineamenti | Concetti | TrasversalitaMeto
 *   Riga 2: col2=294 | col3=588 (wide) | col4=294   → Priorita | Fattori | Velocita
 *   Riga 3: col2=588 (wide) | col3=294 | col4=294   → Attributi | Trasversalita | Strategia
 *
 *   Altezze fisse: riga1=195px | riga2=275px | riga3=195px
 *
 * COLORE ACCENTO: dipende da data.obiettivo
 *   cultura → C.pink   (#E91E8C)
 *   pianeta → C.blue   (#1E78C8)
 *   persone → C.gray   (#AAAAAA)
 *
 * STRATEGIA: ogni entry può avere { colore: "#hex" } per il cerchio numerato.
 *
 * IMMAGINI: metti tutto in public/assets/ → path "/assets/..." funziona senza import.
 */
 
import React from "react";
 
 // ─── PATH ─────────────────────────────────────────────────────────────────────
 const A  = "/assets";
  
 // ─── PALETTE BASE (invariante) ────────────────────────────────────────────────
 const C = {
   pink:   "#CE0F69",
   blue:   "#0084D5",
   dark:   "#222222",
   gray:   "#AAAAAA",
   light:  "#E8E8E8",
   border: "#D8D8D8",
   sep:    "#E8E8E8",
   yellow: "#F0B323"
 };
  
 // colore accento per ogni obiettivo
 const ACCENT = {
   cultura: C.pink,
   pianeta: C.blue,
   persone: C.yellow,
 };
  
 const ff      = "'Helvetica Neue', Arial, sans-serif";
 const ffSerif = "Georgia, 'Times New Roman', serif";
 const arial = "Arial, Arial,sans-serif";
  
 // ─── CONFIG OBIETTIVO ─────────────────────────────────────────────────────────
 const OBIETTIVO_CFG = {
   cultura: { label: "Cultura.", color: C.pink },
   pianeta: { label: "Pianeta.", color: C.blue },
   persone: { label: "Persone.", color: C.yellow },
 };
  
 const BOX_ORDER = ["cultura", "persone", "pianeta"];
 const FALLBACK_BOX_COLOR = "#F5A623"; // arancione – cambia a piacere

 const CONCETTI_LABELS = ["fiducia", "rischio", "flessibilita"];
  
 function concettiImgKey(attivi) {
   if (!attivi || attivi.length === 0) return null;
   return [...attivi].sort((a, b) => a - b).map(i => CONCETTI_LABELS[i]).join("-");
 }
  
 // ─── DIMENSIONI GRIGLIA ───────────────────────────────────────────────────────
 const COL1_W  = 350;   // colonna Obiettivo
 const COL_S   = 294;   // colonna singola
 const COL_D   = 588;   // colonna doppia (2 × COL_S)
 const ROW_H   = [195, 275, 205];  // altezze righe 1-2-3
  
 // ─── STILE LABEL SEZIONE (colore accento dinamico) ────────────────────────────
 function makeSLabel(accent) {
   return {
     fontSize: 12,
     fontWeight: 400,
     letterSpacing: "0.12em",
     textTransform: "uppercase",
     color: accent,
     marginBottom: 8,
     fontFamily: ff,
   };
 }
  
 // ─── STILE CELLA BASE ─────────────────────────────────────────────────────────
 const sCellBase = {
   padding: "12px 14px",
   boxSizing: "border-box",
   fontFamily: ff,
   overflow: "hidden",
 };
  
 // ─── COMPONENTI ATOMICI ───────────────────────────────────────────────────────
 function SLabel({ accent, children }) {
   return <div style={makeSLabel(accent)}>{children}</div>;
 }
  
 function VSep() {
   return (
     <div style={{
       width: 1, height: 22,
       background: C.border,
       margin: "0 10px",
       flexShrink: 0,
     }} />
   );
 }

 function VSep2() {
    return (
      <div style={{
        width: 1, height: 42,
        background: C.border,
        margin: "0 2px",
        flexShrink: 0,
      }} />
    );
  }
  
 function HSep({ indent = 0 }) {
   return <div style={{ height: 1, background: C.sep, marginLeft: indent }} />;
 }
  
 function ArrowSVG({
    fromX = 60,
    color = "#AAAAAA",
    width = 280,
    height = 140,
  }) {
    //const radius = 20; // raggio curva (più alto = più morbido)
    const baseRadius = 20;
    const startX = fromX;
    const startY = 10;
  
    const midY = height * 0.5;
    const endX = 30;
    const endY = height - 10;
  
    const goingLeft = startX > endX;
    const dir = goingLeft ? -1 : 1;

    const dx = Math.abs(startX - endX);
    const radius = Math.min(baseRadius, dx / 2);

    const arrowId = `arrow-${fromX}`;
  
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ display: "block", overflow: "visible" }}
      >
        <defs>        
          <marker
          id={arrowId}
          markerWidth="10"
          markerHeight="10"
          refX="5"
          refY="5"
          orient="auto"
        >
          <path
            d="M2,2 L8,5 L2,8"
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />        
        </marker>
        </defs>
        <path      
          d={`
          M ${startX},${startY}

          L ${startX},${midY - radius}

          Q ${startX},${midY} ${startX + dir * radius},${midY}

          L ${endX - dir * radius},${midY}

          Q ${endX},${midY} ${endX},${midY + radius}

          L ${endX},${endY}
        `}
          fill="none"
          stroke={color}
          strokeWidth={1.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          markerEnd={`url(#${arrowId})`}
        />
      </svg>
    );
  }
  
  function RichText({ segments, accentColor, baseStyle = {} }) {
    if (typeof segments === "string") {
      return <span style={baseStyle}>{segments}</span>;
    }
    return (
      <>
        {segments.map((seg, i) => {
          const style = {
            ...baseStyle,
            ...(seg.accent ? { color: accentColor } : {}),
            ...(seg.bold   ? { fontWeight: 700     } : {}),
            ...(seg.italic ? { fontStyle: "italic" } : {}),
          };
          return <span key={i} style={style}>{seg.t}</span>;
        })}
      </>
    );
 }
 // ─── PANNELLI ─────────────────────────────────────────────────────────────────
  
 // ── 1. OBIETTIVO ───────────────────────────────────────────────────────────── 
 function ObiettivoPanel({ data, accent }) {
    const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
    //console.log("OBS",obiettivi,data);
    const isSingle = obiettivi.length === 1;
    if(!Array.isArray(obiettivi))
        obiettivi=[obiettivi];
    //console.log("OB",obiettivi);
    // ── quadratini: sempre 3 slot fissi (BOX_ORDER) ────────────────────────────
    // Se un obiettivo non è in BOX_ORDER viene colorato con FALLBACK_BOX_COLOR
    const boxColors = BOX_ORDER.map(key => {
      if (obiettivi.includes(key)) {
        return OBIETTIVO_CFG[key]?.color || FALLBACK_BOX_COLOR;
      }
      // controlla se c'è un obiettivo extra fuori dai 3 noti da mettere in questo slot
      return C.light;
    });
    // obiettivi extra (non in BOX_ORDER) → colora ulteriori box
    const extraObiettivi = obiettivi.filter(o => !BOX_ORDER.includes(o));
    // sostituisce i box grigi con i colori degli extra, da sinistra
    let extraIdx = 0;
    const finalBoxColors = boxColors.map(bg => {
      if (bg === C.light && extraIdx < extraObiettivi.length) {
        const extraKey = extraObiettivi[extraIdx++];
        return OBIETTIVO_CFG[extraKey]?.color || FALLBACK_BOX_COLOR;
      }
      return bg;
    });
  
    // ── label obiettivo: uno sotto l'altro se multipli ─────────────────────────
    const labelBlock = (
      <div style={{ margin: "0 0 14px" }}>
        {obiettivi.map((key, i) => {            
          //console.log("CCDDEE",key,i, obiettivi);
          const cfg = OBIETTIVO_CFG[key];
          //console.log("CCDDEE",cfg);
          return (
            <div key={i} style={{
              fontSize: isSingle ? 68 : 68,
              fontWeight: 500,
              color: C.dark,
              lineHeight: 1.05,
              fontFamily: ffSerif,
              whiteSpace: "nowrap",
              paddingBottom: "20px"
            }}>
              {cfg?.label || key}
            </div>
          );
        })}
      </div>
    );
  
    // ── freccia: solo nel caso singolo obiettivo ────────────────────────────────   
    // quadratino: width=90, gap=8 → centro del box i = i*(90+8) + 45
    const activeBoxIdx = BOX_ORDER.indexOf(obiettivi[0]);
    const boxW = 90, boxGap = 8;
    const arrowFromX = activeBoxIdx >= 0
        ? activeBoxIdx * (boxW + boxGap) //+ boxW / 2
        +2
        : boxW / 2;

    // ── missioni ───────────────────────────────────────────────────────────────
    let missioniBlock;
  
    if (isSingle) {
      // CASO SINGOLO: lista numerata completa, numero colorato se attiva
      const accentColor = OBIETTIVO_CFG[obiettivi[0]]?.color || accent;
      //console.log("MIN CHIIA",data.missione, data);
      missioniBlock = (
        <>
          <SLabel accent={accentColor}>Missione</SLabel>
          {data.missione.map((m, i) => (
            <div key={i} style={{
              display: "flex", gap: 5,
              fontSize: 18,
              color: m.attiva ? C.dark : C.gray,
              fontWeight: m.attiva ? 700 : 400,
              marginBottom: 3, lineHeight: 1.35,
            }}>
              {/* numero colorato con accento se attiva, grigio se non attiva */}
              <span style={{
                flexShrink: 0,
                minWidth: 22,
                color: m.attiva ? accentColor : C.gray,
                fontWeight: m.attiva ? 700 : 400,
              }}>
                {m.numero}.
              </span>
              <span style={{fontWeight:  900}} >{m.testo}</span>
            </div>
          ))}
        </>
      );
    } else {
      // CASO MULTIPLO: per ogni obiettivo un blocco con label colorata + solo le missioni attive
      missioniBlock = (
        <>
          {obiettivi.map((key, oi) => {
            const cfg = OBIETTIVO_CFG[key];
            const colore = cfg?.color || FALLBACK_BOX_COLOR;
            const missioniAttive = data.missione.filter(
              m => m.obiettivo === key && m.attiva
            );
            if (missioniAttive.length === 0) return null;
            return (
              <div key={oi} style={{ marginBottom: 10 }}>
                <SLabel accent={colore}>Missione</SLabel>
                {missioniAttive.map((m, mi) => (
                  <div key={mi} style={{
                    display: "flex", gap: 6,
                    fontSize: 18,
                    color: C.dark,
                    fontWeight: 700,
                    marginBottom: 3, lineHeight: 1.35,
                    alignItems: "flex-start",
                  }}>
                    {/* # colorato con il colore dell'obiettivo */}
                    <span style={{
                      color: colore,
                      fontWeight: 700,
                      flexShrink: 0,
                      minWidth: 16,
                    }}>
                      #.
                    </span>
                    <span>{m.testo}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </>
      );
    }
  
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", paddingLeft:"15px" }}>
        <SLabel accent={isSingle ? accent: "black"}>
          {isSingle ? "Obiettivo" : "Ambito / Obiettivo"}
        </SLabel>
  
        {labelBlock}
  
        {/* quadratini colore */}
        <div style={{ display: "flex", gap: 8, marginBottom: 0 }}>
          {finalBoxColors.map((bg, i) => (
            <div key={i} style={{
              width: 90, height: 90,
              background: bg,
              borderRadius: 2,
              flexShrink: 0,
            }} />
          ))}
        </div>
  
        {isSingle
        ? <ArrowSVG fromX={arrowFromX} color={C.gray} width={300} height={160} />
        : <div style={{ height: 20 }} />
        }
  
        {missioniBlock}
      </div>
    );
  }



 // ── 2. ALLINEAMENTI ──────────────────────────────────────────────────────────
 function AllineamentiPanel({ data, accent }) {
   const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
   const isSingle = obiettivi.length === 1;
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={isSingle ? accent: "black"}> Allineamenti</SLabel>
       <img
         src={`${A}/allineamenti/${data.allineamento}.png`}
         alt={`allineamento ${data.allineamento}`}
         style={{ width: "100%", maxWidth: COL_S - 28, display: "block" }}
       />
     </div>
   );
 }
  
 // ── 3. CONCETTI CHIAVE ────────────────────────────────────────────────────────
 function ConceptiPanel({ data, accent }) {
    const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
    const isSingle = obiettivi.length === 1;
   //const key = concettiImgKey(data.concettiAttivi);
   //console.log("WERYU",data.concettiAttivi );
   const key = data.concettiAttivi;
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={isSingle ? accent: "black"}>Concetti Chiave</SLabel>
       <img src={`${A}/concetti_chiave/${data.concettiAttivi}.png`} alt="concetti chiave"
             style={{ width: "88%", maxWidth: COL_S - 28, display: "block" }} />
       
     </div>
   );
 }
  
 // ── 4. TRASVERSALITÀ METODOLOGICHE ───────────────────────────────────────────
 // Layout fisso come screenshot:
 //   - Label in alto a sinistra
 //   - Icone con separatori in basso a sinistra (allineate in basso)
 //   - Testi lista a destra (occupano metà destra della cella wide)
 function TrasversalitaMetoPanel({ data, accent }) {
    const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
    const isSingle = obiettivi.length === 1;
   return (
     <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
       <SLabel accent={isSingle ? accent: "black"}>Trasversalità Metodologiche</SLabel>
  
       {/* corpo: metà sx (icone) | metà dx (testi) */}
       <div style={{ display: "flex", flex: 1, gap: 0 }}>
  
         {/* metà sinistra: icone in basso */}
         <div style={{
           width: COL_S - 28,
           display: "flex",
           alignItems: "center",
           paddingBottom: 8,
           flexShrink: 0,
         }}>
           <div style={{ display: "flex", alignItems: "center" }}>
             {data.trasversalitaMetodologiche.map((item, i) => (                
               <React.Fragment key={i}>
                 <img
                   src={`${A}/trasversalita_metodologiche/${item.key}_${item.attiva ? "nero" : "grigio"}.png`}
                   
                   alt={item.label}
                   style={{ width: 65, height: 66, objectFit: "contain", flexShrink: 0 }}
                 />
                 {i < data.trasversalitaMetodologiche.length - 1 && <VSep2 />}
               </React.Fragment>
             ))}
           </div>
         </div>
  
         {/* metà destra: lista testi, verticalmente centrata */}
         <div style={{
           flex: 1,
           display: "flex",
           flexDirection: "column",
           justifyContent: "center",
           paddingLeft: 16,
         }}>
           {data.trasversalitaMetodologiche.map((item, i) => (
             <div key={i} style={{
               fontSize: item.attiva ? 21 : 18,
               color: item.attiva ? C.dark : C.gray,
               fontWeight: item.attiva ? 700 : 400,
               fontStyle: "italic",
               fontFamily: arial,
               marginBottom: 4,
               //textDecoration: item.attiva ? "underline" : "none",
               textDecorationColor: accent,
             }}>
               {item.label}
             </div>
           ))}
         </div>
       </div>
     </div>
   );
 }
  
 // ── 5. PRIORITÀ TEMATICHE ────────────────────────────────────────────────────
 function PrioritaPanel({ data, accent }) {
    const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
    const isSingle = obiettivi.length === 1;
    return (
        <div style={{ height: "100%" }}>
          <SLabel accent={isSingle ? accent: "black"}>Priorità tematiche</SLabel>
          <img
            src={`${A}/priorita_tematica/${data.prioritaAttiva}.png`}
            alt={`priorita ${data.prioritaAttiva}`}
            style={{ width: "105%", height: "105%", maxWidth: COL_S - 28, display: "block" }}
          />
        </div>
      );  
 }
  
 // ── 6. FATTORI CHIAVE ────────────────────────────────────────────────────────
 function FattoriPanel({ data, accent }) {
   const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
   const isSingle = obiettivi.length === 1;
   //console.log("FATTORI",data,accent); 
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={isSingle ? accent: "black"}>Fattori Chiave</SLabel>
       <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
         {/* icone */}
         <div style={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
           {data.fattoriChiave.map((gruppo, gi) => {            
             //const variant = gruppo.voci.some(v => v.attiva) ? "nero" : "grigio";
             //const variant =  gruppo.voci[ii].attiva ? "nero" : "grigio";
             //console.log("EEHHH",variant);
             return (
               <React.Fragment key={gi}>
                 <div style={{ display: "flex", alignItems: "center", gap: 1, padding: "0px 0" }}>
                   {gruppo.icone.map((ic, ii) => (                 
                     <img key={ii}
                       //src={`${A}/fattori_chiave/${ic.key}_${variant}.png`}
                       src={`${A}/fattori_chiave/${ic.key}_${gruppo.voci[ii].attiva ? "nero" : "grigio"}.png`} 
                       alt={ic.key}
                       style={{ width: 60, height: 60, objectFit: "contain" }}
                     />
                   ))}
                 </div>
                {/**  {gi < data.fattoriChiave.length - 1 && <HSep />} */}
               </React.Fragment>
             );
           })}
         </div>
         {/* testi */}
         <div style={{ flex: 1, minWidth: 0 }}>
           {data.fattoriChiave.map((gruppo, gi) => (
             <React.Fragment key={gi}>
               <div style={{ padding: "0px 0" }}>
                 {gruppo.voci.map((v, vi) => (
                   <div key={vi} style={{
                     fontSize: 11.1,
                     color: v.attiva ? C.dark : C.gray,
                     fontWeight: v.attiva ? 600 : 400,
                     lineHeight: 1.7,
                   }}>
                     {v.testo}
                   </div>
                 ))}
               </div>
             {/*}  {gi < data.fattoriChiave.length - 1 && <HSep />} */}
             </React.Fragment>
           ))}
         </div>
       </div>
     </div>
   );
 }
  
 // ── 7. VELOCITÀ ──────────────────────────────────────────────────────────────

 const PROGETTO_STYLE = {
    position:   "absolute",
    top:        "18%",       // distanza dal bordo superiore dell'immagine
    //left:       "50%",       // centro orizzontale
    left:       "80px",       // centro orizzontale
    right:       "0",  
    //transform:  "translateX(-50%)",
    fontSize:   13,
    fontWeight: 700,
    color:      C.dark,
    fontFamily: ff,
    //whiteSpace: "nowrap",
    whiteSpace: "normal",
    //paddingLeft: "150px",
    lineHeight:  1.4,
    pointerEvents: "none",
  };

 function VelocitaPanel({ data, accent }) {
    const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
    const isSingle = obiettivi.length === 1;
    const voci = data.velocitaVoci || [];
  
    // calcola il nome file immagine dal bitmask
    const imgKey = voci.map(v => v.attiva ? "1" : "0").join("") || "000";
    const imgSrc = `${A}/velocita/${imgKey}.png`;
  
    // testo progetto: solo se prima voce attiva e ha campo progetto
    const firstVoce  = voci[0];
    const showProgetto = firstVoce?.attiva === true && !!firstVoce?.progetto;
    const progettoText = showProgetto ? firstVoce.progetto : null;
  
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <SLabel accent={isSingle ? accent: "black"}>Velocità</SLabel>
  
        {/* immagine + eventuale testo overlay in un contenitore relativo */}
        <div style={{ position: "relative", flex: 1 }}>
          <img
            src={imgSrc}
            alt={`velocita ${imgKey}`}
            style={{
              width: "110%",
              height: "110%",
              objectFit: "contain",
              objectPosition: "top left",
              display: "block",
            }}
          />
          {progettoText && (
            <div style={PROGETTO_STYLE}>
              {progettoText}
            </div>
          )}
        </div>
      </div>
    );
  } 
 // ── 8. ATTRIBUTI DI PROGETTO ─────────────────────────────────────────────────
 function AttributiPanel({ data, accent }) {
    const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
    const isSingle = obiettivi.length === 1;
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={isSingle ? accent: "black"}>Attributi di Progetto</SLabel>
       {data.attributiProgetto.map((item, i) => (        
         <React.Fragment key={i}>
           <div style={{ display:  "flex", alignItems: "center", gap: 10, padding: "0px 0", marginBottom: -5  }}>
             <img
               src={`${A}/attributi_progetto/${item.key}_${item.attiva ? "nero" : "grigio"}.png`}
               alt={item.label}
               style={{ width: 56, height: 56, objectFit: "contain", flexShrink: 0 }}
             />
             <span style={{
               fontSize: item.attiva ? 15 : 12,
               color: item.attiva ? C.dark : C.gray,
               fontWeight: item.attiva ? 600 : 400,
               lineHeight: 1.3,
               //textDecoration: item.attiva ? "underline" : "none",
               textDecorationColor: accent,               
             }}>
               {item.label}
             </span>
           </div>
           {i < data.attributiProgetto.length - 1 && <HSep indent={32} style={{ padding: "0px 0", margin: "0px 0",}}/>}
         </React.Fragment>
       ))}
     </div>
   );
 }
  
 // ── 9. TRASVERSALITÀ ─────────────────────────────────────────────────────────
 function TrasversalitaPanel({ data, accent }) {
    const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
    const isSingle = obiettivi.length === 1;
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={isSingle ? accent: "black"}>Trasversalità tematica</SLabel>
       {data.trasversalita.map((item, i) => (
         <React.Fragment key={i}>
           <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "0px 0", marginBottom: -10,marginTop: -3.5   }}>
             <img
               src={`${A}/trasversalita/${item.key}_${item.attiva ? "nero" : "grigio"}.png`}
               alt={item.label}
               style={{ width: 53, height: 53, objectFit: "contain", flexShrink: 0 }}
             />
             <span style={{
               fontSize: item.attiva ? 15 : 12,
               color: item.attiva ? C.dark : C.gray,
               fontWeight: item.attiva ? 600 : 400,
               lineHeight: 1.3,
               //fontStyle: "italic",
               //fontFamily: ffSerif,
               //textDecoration: item.attiva ? "underline" : "none",
               textDecorationColor: accent,
             }}>
               {item.label}
             </span>
           </div>
           {i < data.trasversalita.length - 1 && <HSep indent={32} />}
         </React.Fragment>
       ))}
     </div>
   );
 }
  
 // ── 10. STRATEGIA DI RIFERIMENTO ─────────────────────────────────────────────
 // Ogni entry può avere { colore: "#hex" } per il colore del cerchio.
 // Se non specificato usa l'accent color dell'obiettivo.
 function StrategiaPanel({ data, accent }) {
    const obiettivi = Array.isArray(data.obiettivo) ? data.obiettivo : [data.obiettivo];
    const isSingle = obiettivi.length === 1;
    const count = data.strategia.length;
    let fontSize = 11;
    let lineHeight = 1.4;
    if (count === 2) {
        lineHeight = 1.6;
    } else if (count === 3) {
        fontSize = 10.5;
        lineHeight = 1.2;
    } else if (count >= 4) {
        lineHeight = 1.0;
        fontSize = 9.5;
    }

    return (
     <div style={{ height: "100%" }}>
       <SLabel accent={isSingle ? accent: "black"}>Principale Elemento di Strategia di Riferimento</SLabel>
       {data.strategia.map((s, i) => {
         // colore cerchio: entry.colore > accent > C.light (se non attiva)
         const circleColor = s.attiva
           ? (s.colore || accent)
           : C.light;
         const accentColor = s.colore || accent; // colore per i segmenti accent:true
         return (
           <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
             <div style={{
               width: 24, height: 24, borderRadius: "50%",
               background: circleColor,
               display: "flex", alignItems: "center", justifyContent: "center",
               flexShrink: 0,
               fontSize: 10,
               color: s.attiva ? "#fff" : C.gray,
               fontWeight: 700,
             }}>
               {s.numero}
             </div>
             <div style={{
              fontSize: fontSize,
              color: s.attiva ? C.dark : C.gray,
              lineHeight: lineHeight,
              flex: 1,
            }}>
             <RichText
                segments={s.testo}
                accentColor={accentColor}
                baseStyle={{
                  fontSize: fontSize,
                  color: s.attiva ? C.dark : C.gray,
                  fontWeight: s.attiva ? 400 : 400,
                }}
              />
              </div>
           </div>
         );
       })}
     </div>
   );
 }
  
 // ─── HELPER CELLA ─────────────────────────────────────────────────────────────
 function Cell({ children, w, h, borderRight = true, borderBottom = true }) {
   return (
     <div style={{
       ...sCellBase,
       width: w,
       height: h,
       flexShrink: 0,
       borderRight:  borderRight  ? `1px solid ${C.border}` : "none",
       borderBottom: borderBottom ? `1px solid ${C.border}` : "none",
     }}>
       {children}
     </div>
   );
 }
  
 // ─── COMPONENTE ROOT ──────────────────────────────────────────────────────────
 export default function SchedaProgetto({ data }) {
   if (!data) return null;
  
   const accent = ACCENT[data.obiettivo] || C.pink;
   const totalW = COL1_W + COL_S + COL_S + COL_D; // 350+294+294+588 = 1526px
  
   return (
     <div style={{
       fontFamily: ff,
       background: "#fff",
       width: totalW,
       //border: `1px solid ${C.border}`,
       borderTop:`1px solid ${C.border}`,
       borderBottom:`1px solid ${C.border}`,
       boxSizing: "border-box",
       overflow: "hidden",
     }}>
  
       {/* ── CORPO GRIGLIA ── */}
       <div style={{ display: "flex", alignItems: "stretch" }}>
  
         {/* COLONNA 1 – Obiettivo, altezza totale delle 3 righe */}
         <Cell
           w={COL1_W}
           h={ROW_H[0] + ROW_H[1] + ROW_H[2]}
           borderRight
           borderBottom={false}
         >
           <ObiettivoPanel data={data} accent={accent} />
         </Cell>
  
         {/* COLONNE 2-4 × 3 RIGHE */}
         <div style={{ display: "flex", flexDirection: "column" }}>
  
           {/* RIGA 1: singola | singola | doppia */}
           <div style={{ display: "flex" }}>
             <Cell w={COL_S} h={ROW_H[0]} borderRight borderBottom>
               <AllineamentiPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_S} h={ROW_H[0]} borderRight borderBottom>
               <ConceptiPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_D} h={ROW_H[0]} borderRight={false} borderBottom>
               <TrasversalitaMetoPanel data={data} accent={accent} />
             </Cell>
           </div>
  
           {/* RIGA 2: singola | doppia | singola */}
           <div style={{ display: "flex" }}>
             <Cell w={COL_S} h={ROW_H[1]} borderRight borderBottom>
               <PrioritaPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_D} h={ROW_H[1]} borderRight borderBottom>
               <FattoriPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_S} h={ROW_H[1]} borderRight={false} borderBottom>
               <VelocitaPanel data={data} accent={accent} />
             </Cell>
           </div>
  
           {/* RIGA 3: doppia | singola | singola */}
           <div style={{ display: "flex" }}>
             <Cell w={COL_S} h={ROW_H[2]} borderRight borderBottom={false}>
               <AttributiPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_S} h={ROW_H[2]} borderRight borderBottom={false}>
               <TrasversalitaPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_D} h={ROW_H[2]} borderRight={false} borderBottom={false}>
               <StrategiaPanel data={data} accent={accent} />
             </Cell>
           </div>
  
         </div>
       </div>
     </div>
   );
 }
  
 